import { useState } from 'react';
import { AlertTriangle, Check, Store as StoreIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useDeviceStore, type DeviceStoreState } from './useDeviceStore';

/**
 * Bandeau magasin (réunion du 09/10/2026, décision 4) : visible en permanence
 * sur la tablette d'un réseau de boutiques, pour que le terrain vérifie le
 * magasin AVANT de commencer. Le magasin est réglé par le back-office : ici
 * on ne choisit que parmi ceux qu'il a ouverts, et seulement s'il y en a
 * plusieurs.
 */
export function StoreBanner({ className }: { className?: string }) {
  const state = useDeviceStore();
  if (state.loading || !state.retail) return null;
  // Aucun magasin ouvert et réseau pas encore migré (`noStore` faux) : rien à
  // dire au terrain — chaque réseau passe aux magasins à son rythme.
  if (state.stores.length === 0 && !state.noStore) return null;
  return <StoreBannerView state={state} className={className} />;
}

export function StoreBannerView({
  state,
  className,
}: {
  state: DeviceStoreState;
  className?: string;
}) {
  const [picking, setPicking] = useState(false);
  const { stores, current, needsChoice, noStore, choose } = state;

  if (noStore) {
    return (
      <div
        role="status"
        className={cn(
          'flex items-center gap-2 px-4 py-2 text-meta font-semibold bg-[rgba(245,158,11,0.12)] text-[#B45309] border-b border-[rgba(245,158,11,0.3)]',
          className,
        )}
      >
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Aucun magasin rattaché — préviens ton back-office.</span>
      </div>
    );
  }

  const open = needsChoice || picking;
  return (
    <div
      className={cn(
        'border-b',
        needsChoice
          ? 'bg-[rgba(245,158,11,0.12)] border-[rgba(245,158,11,0.3)]'
          : 'bg-brand-soft border-hair',
        className,
      )}
    >
      <div className="flex items-center gap-2 px-4 py-2 text-meta">
        <StoreIcon
          className={cn('h-4 w-4 shrink-0', needsChoice ? 'text-[#B45309]' : 'text-brand')}
          aria-hidden="true"
        />
        {current ? (
          <span className="min-w-0 flex-1 text-ink">
            Magasin : <strong className="font-extrabold">{current.name}</strong>
            <span className="text-ink-mute"> · Pas le bon ? Préviens ton back-office.</span>
          </span>
        ) : (
          <span className="min-w-0 flex-1 font-bold text-[#B45309]">
            Choisis ton magasin avant de créer une fiche.
          </span>
        )}
        {stores.length > 1 && current && (
          <button
            type="button"
            onClick={() => setPicking((v) => !v)}
            aria-expanded={picking}
            className="shrink-0 rounded-[var(--radius-sm)] px-2.5 py-1 font-bold text-brand hover:bg-surface/60"
          >
            {picking ? 'Fermer' : 'Changer'}
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-wrap gap-2 px-4 pb-2.5" role="group" aria-label="Magasins">
          {stores.map((s) => {
            const active = current?.id === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  choose(s.id);
                  setPicking(false);
                }}
                aria-pressed={active}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-meta font-bold tap-target',
                  active
                    ? 'bg-brand text-brand-ink border-brand'
                    : 'bg-surface text-ink border-hair hover:bg-surface-alt',
                )}
              >
                {active && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                {s.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
